// V3 — direct table lookups with channel-specific -> 'all' fallback.

import type {
  CostPerRoleRow,
  HubPctRow,
  LevelId,
  LevelWeightRow,
  Model,
  Shore,
  SpendPerFteRow,
  TimeConcentrationRow,
} from '../types.js';
import { groupsForLevels, levelsForGroups } from './level-groups.js';

/** Find a row by (discipline, channel) preferring exact match, falling back to channel='all'. */
function pickByDC<T extends { disciplineId: string; channelId: string }>(
  rows: T[] | undefined,
  disciplineId: string,
  channelId: string,
): T | undefined {
  if (!rows) return undefined;
  return (
    rows.find((r) => r.disciplineId === disciplineId && r.channelId === channelId) ||
    rows.find((r) => r.disciplineId === disciplineId && r.channelId === 'all')
  );
}

function pickByDCL<T extends { disciplineId: string; channelId: string; levelId: LevelId }>(
  rows: T[] | undefined,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
): T | undefined {
  if (!rows) return undefined;
  return (
    rows.find(
      (r) => r.disciplineId === disciplineId && r.channelId === channelId && r.levelId === levelId,
    ) ||
    rows.find(
      (r) => r.disciplineId === disciplineId && r.channelId === 'all' && r.levelId === levelId,
    )
  );
}

export function lookupLevelWeights(
  model: Model,
  disciplineId: string,
  channelId: string,
): LevelWeightRow | undefined {
  return pickByDC(model.levelWeights, disciplineId, channelId);
}

/** Whether a (discipline × channel) is offered as an OGS option in the
 *  intake. Missing eligibility rows default to eligible. */
export function isOgsEligible(
  model: Model,
  disciplineId: string,
  channelId: string,
): boolean {
  const list = model.ogsEligibility;
  if (!list || list.length === 0) return true;
  const entry = list.find(
    (e) => e.disciplineId === disciplineId && e.channelId === channelId,
  );
  return entry ? entry.enabled !== false : true;
}

export function lookupSpendPerFte(
  model: Model,
  disciplineId: string,
  channelId: string,
): SpendPerFteRow | undefined {
  return pickByDC(model.spendPerFte, disciplineId, channelId);
}

/** Per-hub pct lookup for a single hub at (discipline, channel, level).
 *  Used directly by the engine's shore-split stage when the scenario picks
 *  a specific hub (`profile.ogs.{shore}.hubId`), and by the Model UI's
 *  "Hub locations %" tab to render each hub's matrix in isolation. */
export function lookupHubPct(
  model: Model,
  hubId: string,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
): number {
  if (!model.hubPct) return 0;
  const hubRows = model.hubPct.filter((r) => r.hubId === hubId);
  return pickByDCL(hubRows, disciplineId, channelId, levelId)?.pct ?? 0;
}

/** Pick the "default" hub for a shore — the first hub of that shore in
 *  `model.ogsHubs`. Used when callers don't have profile context (model-
 *  level UI views like Version 1) or when a profile leaves `hubId` empty.
 *  Returns undefined when no hub of that shore exists. */
export function defaultHubForShore(
  model: Model,
  shore: 'nearshore' | 'offshore',
): string | undefined {
  return (model.ogsHubs || []).find((h) => h.shore === shore)?.id;
}

/** Model-level offshore pct — the share recommended by the model when no
 *  specific hub is in scope. Resolves the first offshore hub's pct so the
 *  number reconciles with what the user sees as the default in the editor.
 *  Returns 0 if the model has no offshore hub or no matching `hubPct` row. */
export function lookupOffshorePct(
  model: Model,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
): number {
  return lookupShorePctViaDefaultHub(model, 'offshore', disciplineId, channelId, levelId);
}

/** Model-level nearshore pct — same semantics as `lookupOffshorePct`. */
export function lookupNearshorePct(
  model: Model,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
): number {
  return lookupShorePctViaDefaultHub(model, 'nearshore', disciplineId, channelId, levelId);
}

function lookupShorePctViaDefaultHub(
  model: Model,
  shore: 'nearshore' | 'offshore',
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
): number {
  const hubId = defaultHubForShore(model, shore);
  if (!hubId || !model.hubPct) return 0;
  const hubRows = model.hubPct.filter((r) => r.hubId === hubId);
  const row = pickByDCL<HubPctRow>(hubRows, disciplineId, channelId, levelId);
  return row ? Number(row.pct) || 0 : 0;
}

/** Annual cost per FTE for a (shore, discipline, channel, level) cell.
 *
 *  Hub-aware: when `shore` is `'nearshore'` or `'offshore'`, prefers the
 *  matching per-hub row, picking either the requested `hubId` or — when no
 *  hub is supplied — the default hub for that shore (first `OgsHub` of
 *  that shore). Channel-specific → `channel === 'all'` fallback applies.
 *
 *  For `'onshore'`, the row's `hubId` is ignored (onshore is a single
 *  shore with no hub breakdown). */
export function lookupCostPerRole(
  model: Model,
  shore: Shore,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
  hubId?: string,
): number {
  if (!model.costPerRole) return 0;
  if (shore === 'onshore') {
    return findCost(model.costPerRole, shore, undefined, disciplineId, channelId, levelId);
  }
  const targetHubId = hubId ?? defaultHubForShore(model, shore);
  const hubCost = targetHubId
    ? findCost(
    model.costPerRole,
    shore,
    targetHubId,
    disciplineId,
    channelId,
    levelId,
      )
    : 0;
  return hubCost || findCost(model.costPerRole, shore, undefined, disciplineId, channelId, levelId);
}

/** Internal: find an annual cost matching (shore, hubId, discipline, channel,
 *  level), with channel-specific → 'all' fallback. When `hubId` is undefined
 *  the row's hubId is ignored (used for the onshore shore). */
function findCost(
  rows: CostPerRoleRow[],
  shore: Shore,
  hubId: string | undefined,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
): number {
  const hubMatches = (r: CostPerRoleRow) =>
    hubId === undefined ? true : r.hubId === hubId;
  const exact = rows.find(
    (r) =>
      r.shore === shore &&
      hubMatches(r) &&
      r.disciplineId === disciplineId &&
      r.channelId === channelId &&
      r.levelId === levelId,
  );
  if (exact) return Number(exact.annualCost) || 0;
  const fallback = rows.find(
    (r) =>
      r.shore === shore &&
      hubMatches(r) &&
      r.disciplineId === disciplineId &&
      r.channelId === 'all' &&
      r.levelId === levelId,
  );
  return Number(fallback?.annualCost) || 0;
}

/**
 * Suggest an OGS default from `model.hubPct` for UI prefill.
 *
 * Returns a single flat `pct` plus the `levels` that carry any OGS in the
 * model for the hub. When `channelId` is supplied, only that channel's rows
 * are used (falling back to `channel === 'all'`); otherwise the hub's rows are
 * averaged across all channels for the discipline. Used to seed the intake
 * sliders when a user enables OGS / picks a hub. Returns `{ pct: 0, levels: [] }`
 * when the hub has no matching rows.
 */
export function deriveDefaultOgsForDiscipline(
  model: Model,
  disciplineId: string,
  hubId: string | undefined,
  channelId?: string,
): { pct: number; levels: LevelId[] } {
  if (!hubId || !model.hubPct) return { pct: 0, levels: [] };
  let rows = model.hubPct.filter(
    (r) => r.hubId === hubId && r.disciplineId === disciplineId,
  );
  if (channelId !== undefined && channelId !== 'all') {
    const exact = rows.filter((r) => r.channelId === channelId);
    rows = exact.length > 0 ? exact : rows.filter((r) => r.channelId === 'all');
  }
  if (rows.length === 0) return { pct: 0, levels: [] };

  // Average pct per level across channels, then average the non-zero levels
  // into a single representative %; collect the levels that carry any OGS.
  const byLevel = new Map<LevelId, { sum: number; count: number }>();
  for (const r of rows) {
    const acc = byLevel.get(r.levelId) || { sum: 0, count: 0 };
    acc.sum += Number(r.pct) || 0;
    acc.count += 1;
    byLevel.set(r.levelId, acc);
  }

  const levels: LevelId[] = [];
  let pctSum = 0;
  let pctCount = 0;
  for (const [levelId, acc] of byLevel) {
    const avg = acc.count > 0 ? acc.sum / acc.count : 0;
    if (avg > 0) {
      levels.push(levelId);
      pctSum += avg;
      pctCount += 1;
    }
  }

  const pct = pctCount > 0 ? pctSum / pctCount : 0;
  // OGS is chosen per level group, so a group with any OGS level is included whole.
  return { pct, levels: levelsForGroups(groupsForLevels(levels)) };
}

/** Rows of time-concentration shares for a (sub-discipline, level). */
export function timeConcentrationFor(
  model: Model,
  subDisciplineKey: string,
  levelId: LevelId,
): TimeConcentrationRow[] {
  return (model.timeConcentration || []).filter(
    (r) => r.subDisciplineKey === subDisciplineKey && r.levelId === levelId,
  );
}

